import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CircularProgress,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';

import { Strings, taskOverviewColors } from '@app/constants';
import { TaskOverviewCard } from '@app/components/card';
import { DataNotFound, Scrollbar, StatusTag } from '@app/components';
import { InfoStatusCard } from '@app/components/card/info-status-card';
import { StyledTitle } from '@app/modules/dashboard/style';
import { IAddProjectSchema } from '@app/modules/projects';
import { useRequestState } from '@app/hooks';
import { getTasksOverview, selectGetTasksOverviewState } from '@app/modules/dashboard';
import { CustomAvatarGroup, CustomAvatar } from '@app/components/custom-avatar';
import { IProject } from '@app/modules/manager-dashboard';

interface props {
  data?: any;
}
const EmployeeProjectsOverview = ({ data }: props) => {
  const theme = useTheme();
  const [selectedProject, setSelectedProject] = useState<number | null>(null);
  const [resetSelectedMember, setResetSelectedMember] = useState(false);

  const dispatch = useDispatch();

  const { data: Tasks, loading } = useRequestState({
    stateSelector: selectGetTasksOverviewState,
    errorShown: true,
  });

  useEffect(() => {
    if (data && data?.projects?.projects?.length > 0) {
      const firstProjectId = data.projects.projects[0].id;
      setSelectedProject(firstProjectId);
      dispatch(getTasksOverview(firstProjectId));
    }
  }, [data, dispatch]);

  const projectsWithUserNames = data?.projects?.projects?.map((project: any) => ({
    ...project,
    managers: project?.managers
      ?.map((managerId: number) => {
        const user = data?.projects?.users?.find((user: any) => user.id === managerId);
        return user
          ? `${user.firstName as string} ${user.lastName as string}`
          : `User ${managerId}`;
      })
      .join(', '),
  }));

  const handleCardClick = (id: number) => {
    setSelectedProject(id);
    dispatch(getTasksOverview(id));
    setResetSelectedMember(true);
  };

  const tasksColors = taskOverviewColors(theme);

  const isXs = useMediaQuery(theme.breakpoints.only('xs'));
  const isSm = useMediaQuery(theme.breakpoints.only('sm'));

  return (
    <Card sx={{ borderRadius: '5px', margin: '0 2% 2% 2%' }}>
      <Stack flexDirection={{ sm: 'column', md: 'row' }} mb={3}>
        <Card
          sx={{
            width: { xs: '96%', md: '45%' },
            height: { md: '350px' },
            maxHeight: { xs: '350px' },
            margin: '2% 0 0 2%',
            padding: 2,
            pt: 1,
            borderRadius: '5px',
          }}
        >
          <StyledTitle>{Strings.employeeDashborad.myProjects}</StyledTitle>
          <Stack sx={{ height: '100%' }}>
            {projectsWithUserNames && projectsWithUserNames.length > 0 ? (
              <>
                {isXs || isSm ? (
                  <Scrollbar sx={{ maxHeight: { xs: 310 } }}>
                    {projectsWithUserNames?.map((option: IProject) => (
                      <Accordion
                        key={option.id}
                        sx={{ display: { md: 'none', xs: 'inline' } }}
                        onClick={() => {
                          handleCardClick(option.id);
                        }}
                      >
                        <Card
                          sx={{ borderRadius: '5px', margin: 1, height: 'auto' }}
                          onClick={() => {
                            handleCardClick(option.id);
                          }}
                        >
                          <Stack gap={1} justifyContent={'space-between'} direction={'row'} m={0.5}>
                            <Stack direction="row" gap={2}>
                              <img
                                src={option.image}
                                style={{
                                  objectFit: 'contain',
                                  height: '45px',
                                  width: '60px',
                                  alignItems: 'center',
                                }}
                              />
                              <Stack>
                                <Stack> {option.name}</Stack>
                                <Stack>
                                  <CustomAvatarGroup max={4} size="tiny">
                                    {option.teamMembers?.map((member: any, index: number) => (
                                      <CustomAvatar key={index} name={member} color="info" />
                                    ))}
                                  </CustomAvatarGroup>
                                </Stack>
                              </Stack>
                            </Stack>
                            <Stack direction={'row'}>
                              <Stack justifyContent={'center'} alignItems={'center'}>
                                <StatusTag status={option.priority ?? ''} />
                              </Stack>
                              <AccordionSummary
                                expandIcon={<ArrowDropDownIcon />}
                                aria-controls="panel2-content"
                                id="panel2-header"
                              ></AccordionSummary>
                            </Stack>
                          </Stack>
                        </Card>

                        <AccordionDetails>
                          <Stack
                            sx={{
                              width: { sm: '90%', md: '50%' },
                              height: '400px',
                              margin: { xs: '0 2% 0 2%', sm: '0 2% 0 2%', md: '0 2% 0 0%' },
                              marginTop: { xs: '2%', sm: '2%', md: '0%' },
                              borderRadius: '5px',
                            }}
                          >
                            <TaskOverviewCard
                              teamMembers={Tasks?.users ?? []}
                              color={tasksColors}
                              title={Strings.employeeDashborad.taskOverview}
                              taskValue={Tasks ?? []}
                              heading="manager"
                              resetSelectedMember={resetSelectedMember}
                              selectedProject={selectedProject}
                            />
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    ))}
                  </Scrollbar>
                ) : (
                  <Scrollbar
                    sx={{
                      height: { md: 280 },
                      maxHeight: { xs: 280 },
                      display: { xs: 'none', md: 'inline' },
                    }}
                  >
                    {projectsWithUserNames?.map((option: IAddProjectSchema) => (
                      <InfoStatusCard
                        key={option.id}
                        titleName={option.name}
                        subTitleHead={`${Strings.dashboard.managedBy}:`}
                        subTitleValue={option.managers}
                        status={option.priority}
                        image={option.image}
                        onClick={() => {
                          handleCardClick(option.id);
                        }}
                        isSelected={selectedProject === option.id}
                      />
                    ))}
                  </Scrollbar>
                )}
              </>
            ) : (
              <Stack sx={{ height: '100%', justifyContent: 'center' }}>
                <DataNotFound height={200} />
              </Stack>
            )}
          </Stack>
        </Card>
        <Stack
          sx={{
            width: { sm: '90%', md: '50%' },
            height: '350px',
            margin: '0 2% 0 2%',
            marginTop: { xs: '2%', sm: '2%', md: '2%' },
            borderRadius: '5px',
            display: { xs: 'none', md: loading ? 'flex' : 'block' },
            ...(loading && {
              alignItems: 'center',
              justifyContent: 'center',
            }),
          }}
        >
          {loading ? (
            <CircularProgress size={100} />
          ) : (
            <TaskOverviewCard
              teamMembers={Tasks?.users ?? []}
              color={tasksColors}
              title={Strings.employeeDashborad.taskOverview}
              taskValue={Tasks ?? []}
              heading="manager"
              resetSelectedMember={resetSelectedMember}
              setResetSelectedMember={setResetSelectedMember}
              selectedProject={selectedProject}
            />
          )}
        </Stack>
      </Stack>
    </Card>
  );
};

export { EmployeeProjectsOverview };
